/**
 * Handle EPUB resource URLs — create blob/base64 replacements for assets.
 */
import type { Archive } from "./archive";
import type { ManifestItem } from "./packaging";

function escapeRegex(str: string): string {
  return str.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");
}

function substitute(
  content: string,
  urls: string[],
  replacements: string[],
): string {
  for (let i = 0; i < urls.length; i++) {
    if (urls[i] && replacements[i]) {
      content = content.replace(
        new RegExp(escapeRegex(urls[i]), "g"),
        replacements[i],
      );
    }
  }
  return content;
}

function relativePath(from: string, to: string): string {
  const fromParts = from.split("/").slice(0, -1); // directory of `from`
  const toParts = to.split("/");
  // Find common prefix
  let common = 0;
  while (
    common < fromParts.length &&
    common < toParts.length &&
    fromParts[common] === toParts[common]
  ) {
    common++;
  }
  const ups = fromParts.length - common;
  const rest = toParts.slice(common);
  return (ups > 0 ? "../".repeat(ups) : "") + rest.join("/");
}

export class Resources {
  private archive: Archive;
  private resolver: (href: string) => string;
  private replacementStyle: "base64" | "blobUrl" | "none";

  manifest: Record<string, ManifestItem> = {};
  html: ManifestItem[] = [];
  assets: ManifestItem[] = [];
  css: ManifestItem[] = [];
  urls: string[] = [];
  cssUrls: string[] = [];
  replacementUrls: string[] = [];

  constructor(
    manifest: Record<string, ManifestItem>,
    options: {
      archive: Archive;
      resolver: (href: string) => string;
      replacements?: "base64" | "blobUrl" | "none";
    },
  ) {
    this.archive = options.archive;
    this.resolver = options.resolver;
    this.replacementStyle = options.replacements ?? "base64";
    this.process(manifest);
  }

  private process(manifest: Record<string, ManifestItem>): void {
    this.manifest = manifest;
    const items = Object.values(manifest);

    this.html = items.filter(
      (i) => i.type === "application/xhtml+xml" || i.type === "text/html",
    );
    this.assets = items.filter(
      (i) => i.type !== "application/xhtml+xml" && i.type !== "text/html",
    );
    this.css = items.filter((i) => i.type === "text/css");

    this.urls = this.assets.map((i) => i.href);
    this.cssUrls = this.css.map((i) => i.href);
    this.replacementUrls = [];
  }

  /**
   * Create blob/base64 URLs for all non-HTML assets.
   */
  async replacements(): Promise<string[]> {
    if (this.replacementStyle === "none") {
      this.replacementUrls = [...this.urls];
      return this.urls;
    }

    const results = await Promise.all(
      this.urls.map(async (href) => {
        try {
          const absolute = this.resolver(href);
          return await this.archive.createUrl(absolute, {
            base64: this.replacementStyle === "base64",
          });
        } catch (err) {
          console.error("Resource replacement failed:", href, err);
          return null;
        }
      }),
    );

    this.replacementUrls = results.map((r) => (typeof r === "string" ? r : ""));
    return this.replacementUrls;
  }

  /**
   * Replace internal URLs within CSS files with blob/base64 URLs,
   * then replace the CSS file's own entry in replacementUrls.
   */
  async replaceCss(): Promise<void> {
    const promises = this.cssUrls.map(async (href) => {
      const absolute = this.resolver(href);

      let text: string;
      try {
        text = await this.archive.getText(absolute);
      } catch {
        return; // CSS file not found, skip
      }

      // Compute relative paths from CSS to each asset
      const relUrls = this.urls.map((assetHref) => {
        const resolvedAsset = this.resolver(assetHref);
        return relativePath(absolute, resolvedAsset);
      });

      // Substitute relative asset URLs with replacement URLs
      text = substitute(text, relUrls, this.replacementUrls);

      // Create new blob/base64 URL for the rewritten CSS
      let newUrl: string;
      if (this.replacementStyle === "base64") {
        newUrl = `data:text/css;base64,${btoa(unescape(encodeURIComponent(text)))}`;
      } else {
        newUrl = URL.createObjectURL(new Blob([text], { type: "text/css" }));
      }

      // Replace the CSS entry in replacementUrls
      const idx = this.urls.indexOf(href);
      if (idx > -1) this.replacementUrls[idx] = newUrl;
    });

    await Promise.all(promises);
  }

  /**
   * Substitute asset URLs in section content.
   */
  substituteContent(content: string, sectionUrl?: string): string {
    let relUrls: string[];
    if (sectionUrl) {
      relUrls = this.urls.map((href) => {
        const resolved = this.resolver(href);
        return relativePath(sectionUrl, resolved);
      });
    } else {
      relUrls = this.urls;
    }

    const nonEmptyReplacements = this.replacementUrls.filter(Boolean).length;
    // Find which relative URLs actually appear in the content
    const matchingUrls = relUrls.filter((u) => u && content.includes(u));
    console.debug("[epub:resources] substituteContent", {
      sectionUrl,
      contentLength: content.length,
      totalAssets: this.urls.length,
      nonEmptyReplacements,
      matchingUrlsInContent: matchingUrls.length,
      matchingSample: matchingUrls.slice(0, 5),
      replacementsSample: matchingUrls.slice(0, 5).map((u) => {
        const i = relUrls.indexOf(u);
        return {
          from: u,
          to: this.replacementUrls[i]?.slice(0, 50) || "EMPTY",
        };
      }),
    });

    const result = substitute(content, relUrls, this.replacementUrls);

    // Check if any substitutions actually happened
    const changed = result !== content;
    if (!changed && matchingUrls.length > 0) {
      console.warn("[epub:resources] substituteContent found matching URLs but content unchanged!", {
        matchingUrls,
        relUrlsSample: relUrls.slice(0, 10),
        replacementUrlsSample: this.replacementUrls.slice(0, 5).map((u) => u?.slice(0, 50)),
      });
    }
    console.debug("[epub:resources] substituteContent result", {
      changed,
      resultLength: result.length,
    });

    return result;
  }

  /**
   * Get replacement URL for a specific resource path.
   */
  get(path: string): string | undefined {
    const idx = this.urls.indexOf(path);
    if (idx === -1) return undefined;
    return this.replacementUrls[idx];
  }

  destroy(): void {
    this.manifest = {};
    this.html = [];
    this.assets = [];
    this.css = [];
    this.urls = [];
    this.cssUrls = [];
    this.replacementUrls = [];
  }
}
